import React, { useState, useEffect, useCallback } from 'react'
import { IApplicantScoreHypoGroup, IApplicantProgram, IProgramsDropdownOption, StudentPROptionsCategoryGroup, StudentPROptionsCategoryGroupLabel, ITag } from '../../interfaces/student';
import { useDispatch } from 'react-redux';
import { openModal } from '../../store/slices/modal';

import { useGetStudentApplicantScores, useGetStudentApplicantPrograms, useGetStudentApplicantScoreHypoGroups } from '../../hooks/misc';
import moment from 'moment';
import { SVGIcon } from '../wrappers/SVGIcon';
import { Icons } from '../../data/Assets';
import ProgramsDropDown from '../sub_components/student/immigration/ProgramsDropDown';
import ScoreHypoCard from '../sub_components/student/immigration/ScoreHypoCard';
import OtherProgramCard from '../sub_components/student/immigration/OtherProgramCard';

interface PROptionsProps {
    linked: boolean;
    studentId: number;
    studentName: string;
}

const PROptions = (props: PROptionsProps) => {
    const { studentId, linked, studentName } = props;

    const dispatch = useDispatch();

    const categoryGroupOptions: IProgramsDropdownOption[] = [
        {
            value: StudentPROptionsCategoryGroup.ExpressEntry,
            label: StudentPROptionsCategoryGroupLabel.ExpressEntry
        },
        {
            value: StudentPROptionsCategoryGroup.OtherPR,
            label: StudentPROptionsCategoryGroupLabel.OtherPR
        },
        {
            value: StudentPROptionsCategoryGroup.Temporary,
            label: StudentPROptionsCategoryGroupLabel.Temporary
        }
    ];

    const [chosenCategoryGroup, setChosenCategoryGroup] = useState<IProgramsDropdownOption>(categoryGroupOptions[0]);
    const [chosenProgram, setChosenProgram] = useState<IApplicantProgram | null>(null);
    const [programs, setPrograms] = useState<IApplicantProgram[]>([]);
    const [scoreHypos, setScoreHypos] = useState<IApplicantScoreHypoGroup[]>([]);
    const [score, setScore] = useState(0);
    const [totalScore, setTotalScore] = useState(0);
    const [filters, setFilters] = useState<ITag[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<ITag[]>([]);

    const { data: applicantScores } = useGetStudentApplicantScores(studentId, linked);
    const { data: applicantPrograms } = useGetStudentApplicantPrograms(studentId, chosenCategoryGroup.value, linked);
    const { data: scoreHypoGroups } = useGetStudentApplicantScoreHypoGroups(studentId, linked);

    const onChangeSelect = (dropDownType: 'category_group' | 'program', option: IProgramsDropdownOption) => {
        if (dropDownType === 'category_group') {
            setChosenCategoryGroup(option);
        } else if (dropDownType === 'program') {
            setChosenProgram(programs.find(program => program.id.toString() === option.value) || null);
        }
    }

    const getDifficulty = (score: number, totalScore: number) => {
        if (score >= totalScore)
            return 'easy';
        else if (totalScore - score <= 100)
            return 'medium';
        else
            return 'hard';
    }

    useEffect(() => {
        if (chosenCategoryGroup.value === StudentPROptionsCategoryGroup.OtherPR) {
            setFilters([{ id: 0, name: "Favourite" }, ...(applicantPrograms?.main_program_filters || [])]);
            setSelectedFilters([]);
        }

        if (applicantPrograms?.main?.length) {
            setPrograms(applicantPrograms.main);
            setChosenProgram(applicantPrograms.main[0]);
        };
    }, [applicantPrograms, chosenCategoryGroup]);

    useEffect(() => {
        if (applicantScores?.main?.length && chosenProgram?.results?.length) {
            setScore(applicantScores.main[0].score);
            setTotalScore(chosenProgram.results[0].draw_points);
        };
    }, [chosenProgram, applicantScores]);

    useEffect(() => {
        if (chosenCategoryGroup.value === StudentPROptionsCategoryGroup.ExpressEntry) {
            setFilters([{ id: 0, name: "Favourite" }, ...(scoreHypoGroups?.main_filters || [])]);
            setSelectedFilters([]);
        }

        if (score >= totalScore) {
            setScoreHypos([]);
        } else if (scoreHypoGroups?.main?.length) {
            setScoreHypos(scoreHypoGroups.main.filter(scoreHypo => scoreHypo.score >= totalScore));
        };
    }, [scoreHypoGroups, chosenCategoryGroup, chosenProgram, score, totalScore]);

    const onFilterButtonClick = () => {
        dispatch(openModal({
            name: 'FILTER_MODAL',
            data: {
                view: 'FILTER',
                filterText: chosenCategoryGroup.value === StudentPROptionsCategoryGroup.ExpressEntry ? 'Filter Recommendations' : 'Filter Programs',
                filters: filters,
                selectedFilters: selectedFilters,
                setSelectedFilters: setSelectedFilters,
            }
        }));
    };

    const getFilteredScoreHypos = useCallback(() => {
        const filteredScoreHypos = scoreHypos.filter(item => {
            const isFavoriteFilter = selectedFilters.find(tag => tag.id === 0) ? item.favorite : true;
            const isProgramMatch = selectedFilters.length ? selectedFilters.filter(filterTag => filterTag.id !== 0).every(filterTag => item.tag.find(tag => tag.id === filterTag.id) !== undefined) : true;

            const isFilterMatch = isFavoriteFilter && isProgramMatch;

            return isFilterMatch;
        });

        return filteredScoreHypos;
    }, [selectedFilters, scoreHypos]);

    const getFilteredPrograms = useCallback(() => {
        const filteredPrograms = programs.filter(item => {
            const isFavoriteFilter = selectedFilters.find(tag => tag.id === 0) ? item.favorite : true;
            const isProgramMatch = selectedFilters.length ? selectedFilters.filter(filterTag => filterTag.id !== 0).every(filterTag => item.program_tags.find(programTag => programTag.tag_id === filterTag.id) !== undefined) : true;

            const isFilterMatch = isFavoriteFilter && isProgramMatch;

            return isFilterMatch;
        });

        return filteredPrograms;
    }, [selectedFilters, programs]);

    const scoreHyposFiltered = getFilteredScoreHypos();
    const programsFiltered = getFilteredPrograms();

    return (
        <div className='immersion__details_pr_options__container'>
            {linked
                ? <>
                    <div className='immersion__pr_options__score__container'>
                        <h3 className='immersion__pr_options__score__container__header_title'>
                            Chances of getting PR
                        </h3>

                        <ProgramsDropDown
                            value={chosenCategoryGroup}
                            options={categoryGroupOptions}
                            onChangeSelect={(option) => onChangeSelect('category_group', option as IProgramsDropdownOption)}
                        />

                        {chosenCategoryGroup.value === StudentPROptionsCategoryGroup.ExpressEntry &&
                            <div className='immersion__pr_options__score'>
                                <h5 className='immersion__pr_options__score__worker_name'>
                                    {studentName}'s Score
                                </h5>

                                <div className='immersion__pr_options__score_points'>
                                    <h3 className={`immersion__pr_options__score_points__value immersion__score_${getDifficulty(score, totalScore)}_color`}>
                                        {score} points
                                    </h3>
                                </div>

                                {programs.length > 0 &&
                                    <ProgramsDropDown
                                        value={{
                                            value: chosenProgram?.id.toString() || '',
                                            label: chosenProgram?.name || '',
                                            difficulty: getDifficulty(score, chosenProgram?.results[0].draw_points || 0)
                                        }}
                                        options={programs.map(program => ({
                                            value: program.id.toString(),
                                            label: program.name,
                                            difficulty: getDifficulty(score, program.results[0].draw_points || 0)
                                        }))}
                                        onChangeSelect={(option) => onChangeSelect('program', option as IProgramsDropdownOption)}
                                    />
                                }

                                <hr className='immersion__pr_options__score_divider' />

                                {chosenProgram &&
                                    <div className='immersion__pr_options__program_details'>
                                        <div className='immersion__pr_options__program_details__header'>
                                            <h5 className='immersion__pr_options__draws_header_title'>
                                                {chosenProgram.results[0].draw_date
                                                    ? `Recent draw ${moment.utc(chosenProgram.results[0].draw_date).format('MMMM DD, YYYY')}`
                                                    : 'No recent draws'
                                                }
                                            </h5>

                                            <h5 className='immersion__pr_options__draws_points'>
                                                {chosenProgram.results[0].draw_points} points
                                            </h5>
                                        </div>

                                        <div className='immersion__pr_options__program_details__content'>
                                            <h5>
                                                {chosenProgram.description}
                                            </h5>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    </div>

                    <div className='immersion__pr_options__score__recommendations__container'>
                        <div className='immersion__pr_options__recommendations__header'>
                            <span className='immersion__pr_options__recommendations__header_title'>
                                {chosenCategoryGroup.value === StudentPROptionsCategoryGroup.ExpressEntry
                                    ? 'Recommendations'
                                    : `${chosenCategoryGroup.label} Programs`
                                }
                            </span>

                            <button className='immersion__pr_options__recommendations__filter_btn' onClick={onFilterButtonClick}>
                                <SVGIcon src={Icons.Filter} color='primary' />
                                <h5>Filter</h5>
                            </button>
                        </div>

                        <div className='immersion__pr_options__recommendations_list'>
                            {chosenCategoryGroup.value === StudentPROptionsCategoryGroup.ExpressEntry &&
                                <>
                                    {scoreHyposFiltered.length === 0
                                        ? <div className='immersion__pr_options__no_recommendations'>
                                            <span> No recommendations available.
                                                {chosenCategoryGroup.value === StudentPROptionsCategoryGroup.ExpressEntry
                                                    && getDifficulty(score, totalScore) === 'easy'
                                                    ? 'You have a good chance of getting PR.'
                                                    : ''
                                                }
                                            </span>
                                        </div>
                                        : scoreHyposFiltered.map((scoreHypo, index) => (
                                            <ScoreHypoCard
                                                key={index}
                                                scoreHypoGroup={scoreHypo}
                                            />
                                        ))
                                    }
                                </>
                            }
                            {chosenCategoryGroup.value === StudentPROptionsCategoryGroup.OtherPR &&
                                <>
                                    {programsFiltered.length === 0
                                        ? <div className='immersion__pr_options__no_recommendations'>
                                            <span> No recommendations available. </span>
                                        </div>
                                        : programsFiltered.map((program, index) => (
                                            <OtherProgramCard
                                                key={index}
                                                program={program}
                                            />
                                        ))
                                    }
                                </>
                            }
                            {chosenCategoryGroup.value === StudentPROptionsCategoryGroup.Temporary &&
                                <div className='immersion__pr_options__no_recommendations'>
                                    <span> No recommendations available. </span>
                                </div>
                            }
                        </div>
                    </div>
                </>
                : <div className='immersion__pr_options__score__container'>
                    <h3 className='immersion__pr_options__score__container__header_title'>
                        Please link the worker to view PR options
                    </h3>
                </div>
            }

        </div >
    )
}

export default PROptions;